/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import DatePicker from 'react-date-picker';
import * as emotion from "emotion";
import {FONTS} from "@res/fonts";
import {COLORS} from "@res/colors";
import dayjs from "../../../utils/dayjs";
import {generateHex} from "@intuitionrobotics/ts-common/utils/random-tools";
import {ICONS} from "@res/icons";

const inputTextLabel = emotion.css(
    {
        marginBottom: 5
    }
);

const calendarStyling = emotion.css(
    {
        position: "absolute",
        top: "40px"
    }
)

const dividerStyle = emotion.css(
    {
        margin: "0 5px 0 5px"
    })

const timeInputStyle = emotion.css(
    {
        fontFamily: "Inter-Regular",
        fontSize: "14",
        border: `1px solid ${COLORS.neutral_gray_2}`,
        borderRadius: 4,
        width: "40px",
        height: "30px",
        textAlign: "center"
    })

const periodInputStyle = emotion.css(
    {
        fontFamily: "Inter-Regular",
        fontSize: "14",
        border: `1px solid ${COLORS.neutral_gray_2}`,
        borderRadius: 4,
        width: "45px",
        height: "35px",
        marginLeft: "5px",
        textAlign: "center"
    })


const calendarStyle = emotion.css(
    {
        width: "100%",
        fontFamily: "Inter-Regular",
        fontSize: "14",
        border: `1px solid ${COLORS.neutral_gray_2}`,
        borderRadius: 4
    }
)

type State = {
    times: SelectedTime[]
    date: Date | undefined
}

type SelectedTime = {
    id: string,
    hours: string,
    minutes: string,
    period: "AM" | "PM",
}

type Props = {
    _key: string
    initialTimes: Date[],
    minDate: Date,
    maxDate: Date,
    onChange: (dates: Date[] | undefined) => void,
    allowMultipleTimes?: boolean
}

export class DateTimePicker
    extends React.Component<Props, State> {
    private readonly ownRef: React.RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);
        this.ownRef = React.createRef<HTMLDivElement>();

        const timesObject = this.getInitialTimesFromProps();
        const date = new Date();
        date.setSeconds(0);

        this.state = {
            times: timesObject,
            date: date
        };
    }

    getInitialTimesFromProps() {
        if (!this.props.initialTimes.length)
            return [this.getSelectedTimeFromDate()];

        return this.props.initialTimes.map((item) => this.getSelectedTimeFromDate(item));
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (prevProps._key !== this.props._key) {
            const date = new Date();
            date.setSeconds(0);
            this.setState({date, times: this.getInitialTimesFromProps()});
        }
    }

    render() {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    marginBottom: "1em",
                    width: "100%"
                }}
                ref={this.ownRef}
                onChange={this.inputValidation}
            >
                <div className={inputTextLabel}>
                    {FONTS.reg_16_black(`Date`)}:
                </div>
                <div className={inputTextLabel}>
                    <DatePicker
                        onChange={this.changeDate}
                        value={this.state.date}
                        className={calendarStyle}
                        showLeadingZeros={true}
                        minDate={this.props.minDate}
                        maxDate={this.props.maxDate}
                        clearIcon={null}
                        calendarClassName={calendarStyling}
                        returnValue={undefined}
                    />
                </div>
                <div style={{marginTop: 5, marginBottom: 4, display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    {FONTS.reg_16_black(`Time`)}
                    {
                        this.props.allowMultipleTimes && this.state.times.length < 6 && <div onClick={this.addNewTime}>
                            {FONTS.reg_11(`+ Add more`, {cursor: 'pointer'})}
                        </div>
                    }
                </div>
                {
                    this.state.times.map((item, index) => this.timeComponent(item, index))
                }
            </div>
        );
    }

    private addNewTime = () => {
        this.setState({times: [...this.state.times, this.getSelectedTimeFromDate()]});
    }

    private timeComponent = (item: SelectedTime, index: number) => {
        return <div key={item.id} style={{display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4}}>
            <div>
                <input
                    className={timeInputStyle}
                    type={"text"}
                    onFocus={(event) => {
                        event.target.select()
                    }}
                    onChange={(event: any) => {
                        // we make the date invalid to prevent the user from submitting an invalid date, when
                        // they have not left the hour input field
                        this.props.onChange(undefined);
                        let hoursValue: string = event.target.value;
                        if (hoursValue.length > 2) {
                            hoursValue = hoursValue.slice(2);
                        }
                        const hours = parseInt(hoursValue, 10);
                        if ((hours <= 12) && (hours >= 0)) {
                            const updatedTimes = this.state.times;
                            updatedTimes[index].hours = hoursValue;
                            this.setState({times: updatedTimes})
                        }
                    }}
                    onBlur={(event) => this.validateAndUpdateHours(event, item, index)}
                    value={item.hours}
                >
                </input>
                <span className={dividerStyle}>:</span>
                <input
                    className={timeInputStyle}
                    type={"text"}
                    onFocus={(event) => {
                        event.target.select()
                    }}
                    value={item.minutes}
                    onChange={(event) => this.changeMinutes(event, item, index)}>
                </input>
                <select name="period" className={periodInputStyle} value={item.period} onChange={(event) => this.changePeriod(event, item, index)}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                </select>
            </div>
            {
                this.state.times.length > 1 && <div style={{marginLeft: 15, cursor: 'pointer'}} onClick={() => this.deleteTime(item.id)}>
                    {ICONS.x(COLORS.red, 20)}
                </div>
            }
        </div>
    }

    private deleteTime = (id: string) => {
        const updatedTimes = this.state.times.filter(item => item.id !== id);

        this.setState({times: updatedTimes})
    }

    private generateDate = (_date: Date, hours: string, minutes: string, period: "AM" | "PM") => {
        // convert hours from 12-hour format to 24-hour format and set the date
        const date = new Date(_date);
        let hoursValue = hours;
        if (hours === "12") {
            hoursValue = "00";
        }
        if (period === "PM") {
            hoursValue = (parseInt(hoursValue, 10) + 12).toString();
        }
        date.setHours(parseInt(hoursValue, 10));
        date.setMinutes(parseInt(minutes), 0);
        return date;
    }
    private changeDate = (value: Date | (Date | null)[] | null) => {

        if (value instanceof Date) {
            if (!value) {
                this.props.onChange(undefined);
                this.setState({date: undefined});
                return;
            }
            this.setState((prevState) => {
                if (!prevState.times) {
                    this.props.onChange(undefined);
                    return {date: value};
                }

                this.props.onChange(prevState.times.map((item) => this.generateDate(value, item.hours, item.minutes, item.period)));
                return {date: this.generateDate(value, prevState.times[0].hours, prevState.times[0].minutes, prevState.times[0].period)};
            });
        } else {
            // Handle invalid value
            console.error('Invalid date value:', value);
        }
    }
    private validateDateFields = (): boolean => {
        // use reference to element to get child elements with class name

        const month = this.ownRef.current?.getElementsByClassName(
            "react-date-picker__inputGroup__input react-date-picker__inputGroup__month")[0] as HTMLInputElement | undefined;
        const day = this.ownRef.current?.getElementsByClassName(
            "react-date-picker__inputGroup__input react-date-picker__inputGroup__day")[0] as HTMLInputElement | undefined;
        const year = this.ownRef.current?.getElementsByClassName(
            "react-date-picker__inputGroup__input react-date-picker__inputGroup__year")[0] as HTMLInputElement | undefined;

        if (!month || !day || !year) {
            return false
        }

        // In case the user has entered an empty field we set the date to null
        if (month.value.length === 0 || day.value.length === 0 || year.value.length === 0) {
            this.setState({date: undefined});
            this.props.onChange(undefined);
            return false;
        }

        // Add leading zeros to the date fields
        const monthValue = month.value.length === 1 ? "0" + month.value : month.value;
        const dayValue = day.value.length === 1 ? "0" + day.value : day.value;

        const date = `${monthValue}/${dayValue}/${year.value}`;

        return dayjs(date, "MM/DD/YYYY", true).isValid();
    }
    /**
     * This function is called when the user changes the date or time fields. We
     * perform validation to ensure the values are valid. If they are not, we
     * notify the parent component that the date is undefined by setting the parents
     * onFilter function to undefined.
     */
    private inputValidation = () => {
        const hasValidDate = this.validateDateFields();
        if (!hasValidDate) {
            this.props.onChange(undefined);
            this.setState({date: undefined});
        }
    }
    private changeMinutes = (event: any, selectedTime: SelectedTime, index: number) => {
        let minutesValue = event.target.value;
        const minutes = parseInt(minutesValue);

        if ((minutes >= 0) && (minutes <= 59)) {
            if (minutes < 10) {
                minutesValue = "0" + minutes.toString();
            } else {
                minutesValue = minutes.toString();
            }

        } else {
            return;
        }

        const newDate = this.state.date ? this.generateDate(this.state.date, selectedTime.hours, minutesValue, selectedTime.period) : undefined;

        const updatedTimes = this.state.times;
        updatedTimes[index].minutes = minutesValue

        if (newDate)
            this.props.onChange(updatedTimes.map((item) => this.generateDate(newDate, item.hours, item.minutes, item.period)));

        this.setState({date: newDate, times: updatedTimes});
    }
    private validateAndUpdateHours = (event: any, selectedTime: SelectedTime, index: number) => {
        let hoursValue = event.target.value;
        const hours = parseInt(hoursValue);
        if ((hours > 0) && (hours <= 12)) {
            if (hours < 10) {
                hoursValue = "0" + hours.toString();
            } else {
                hoursValue = hours.toString();
            }
        } else {
            hoursValue = "12";
        }
        const newDate = this.state.date ? this.generateDate(this.state.date, hoursValue, selectedTime.minutes, selectedTime.period) : undefined;

        const updatedTimes = this.state.times;
        updatedTimes[index].hours = hoursValue;

        if (newDate)
            this.props.onChange(updatedTimes.map((item) => this.generateDate(newDate, item.hours, item.minutes, item.period)));

        this.setState({date: newDate, times: updatedTimes});
    }

    private changePeriod = (event: any, selectedTime: SelectedTime, index: number) => {
        const period = event.target.value;

        const updatedTimes = this.state.times;
        updatedTimes[index].period = period;

        this.setState({times: updatedTimes});
        if (this.state.date) {
            const newDate = this.generateDate(this.state.date, selectedTime.hours, selectedTime.minutes, period);
            if (newDate)
                this.props.onChange(updatedTimes.map((item) => this.generateDate(newDate, item.hours, item.minutes, item.period)));
            this.setState({date: newDate});
        }
    }

    private getSelectedTimeFromDate = (date?: Date): SelectedTime => {
        const dateMoment = dayjs(date);
        return {
            id: generateHex(32),
            hours: dateMoment.format("hh"),
            minutes: dateMoment.format("mm"),
            period: dateMoment.format("A") as "AM" | "PM"
        };
    }
}
